// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## noteReadingPiano.js
//
// Students play what's shown on the staff on a piano keyboard.
import type { ClefName } from 'shared/definitions/musicTheory/clefs';
import type { AnswerAndData } from 'shared/definitions/answer-and-data';
import InteractivePrototype from './interactivePrototype';
import type { InteractiveOptions } from './interactivePrototype';

import * as Skills from '../uTheory.Skills/skills';
import QuestionGenerator from '../uTheory.Skills/questionGenerator';
import utils from '../utils';
import Music from '../uTheory.Notation/music';
import NoteReadingQuestion from './noteReadingPianoQuestion';
import type { Note, NoteHolder } from '../notation';

interface NoteReadingPianoOptions extends InteractiveOptions {
  lowestNote?: string | Note;
  highestNote?: string | Note;
  clef?: ClefName;
  ignoreOctave?: boolean;
}

export default class NoteReadingPiano extends InteractivePrototype {
  declare options: NoteReadingPianoOptions;

  declare questions: NoteReadingQuestion[];

  declare currentQuestion: NoteReadingQuestion;

  lastMidi: number | undefined;

  checkingMidiNumber: number | undefined;

  questionIsFinished: boolean;

  constructor(displayDiv: HTMLDivElement, options: NoteReadingPianoOptions) {
    super(displayDiv, options);
    this.targetSpeedFromSkills = NoteReadingPiano.registry
      .map((r) => r.skills)
      .reduce((a, b) => a.concat(b));
    this.options = options;
    this.settings = {
      instructions: true,
      notation: {
        ariaLive: 'alert',
        ariaTitle: {
          lineOrSpace: true,
          accidental: true,
          octave: false,
          letterName: false,
          measureNumber: false,
        },
      },
      piano: {
        highlightPressed: false,
        ariaLive: 'off',
      },
      checkMark: {},
      player: {},
      progressTracker: true,
      oneTryOnly: false,
    };

    this.layout = {
      notation: { percent: 35 },
      piano: { percent: 65 },
    };
    this.lastMidi = undefined;
    this.questionIsFinished = false;

    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: NoteReadingPianoOptions): Promise<void> {
    options.maxQuestions = options.maxQuestions || 6;
    // Set piano lowest & highest to fit range:
    // changed automatically if in drill mode.
    utils.merge(this.settings.piano as object, {
      lowestNote: this.options.lowestNote,
      highestNote: this.options.highestNote,
    });

    // now call the prototype's init function:
    return super.init(displayDiv, options);
  }

  generateQuestions(questions: string[]): void {
    if (!this.options.testMode && this.options.drill) {
      this.questions = this.generateDrillQuestions();
    } else if (!this.options.testMode) {
      if (!this.options.questions) {
        throw new Error(
          "Can't find questions. The interactives needs manual questions to be run in non-test non-drill mode."
        );
      }
      this.questions = this.options.questions.map((question: string, questionNumber: number) =>
        this.processQuestion(question, questionNumber)
      );
    } else {
      this.questions = questions.map((question: string, questionNumber: number) =>
        this.processQuestion(question, questionNumber)
      );
    }

    let maxQ;
    if (this.options.drill) {
      maxQ = this.options.maxQuestions || 6;
    } else {
      maxQ = this.questions.length;
    }

    this.questionPrompt = this.options.questionPrompt || 'Play this music:';
    if (!this.options.testMode) this.questionPrompt += `<br /><small>{question} of ${maxQ}</small>`;
  }

  processQuestion(questionString: string, questionNumber: number): NoteReadingQuestion {
    return new NoteReadingQuestion(questionString, this.options.clef || 'treble', questionNumber);
  }

  initializeMusic(): void {
    this.music = Music.fromString('key:C\nclef:treble C4/w');
  }

  generateDrillQuestions(): NoteReadingQuestion[] {
    const generator = new QuestionGenerator();

    const skills = this.options.skills || [];
    const skillObjects = [];

    // Default treble range:
    let highNote = 'C5';
    let lowNote = 'C4';

    if (this.options.clef === 'bass') {
      highNote = 'A4';
      lowNote = 'G2';
    }

    let lowSet = false;
    let highSet = false;
    // Tracks whether accidentals are present:
    let accidentalsSet = false;
    // Tracks whether naturals are present:
    let naturalsSet = false;
    // Tracks whether the natural skills is included (meaning we need both acc & naturals)
    let naturalSkillSet = false;

    skills
      .map((skill) => Skills.makeString(skill))
      .forEach((skillString) => {
        let probability;
        switch (skillString) {
          case 'piano_c_g':
            probability = 7.5;
            naturalsSet = true;
            break;
          case 'piano_a_b':
            probability = 3;
            naturalsSet = true;
            break;
          case 'sharps':
            accidentalsSet = true;
            probability = 5;
            break;
          case 'flats':
            accidentalsSet = true;
            probability = 5;
            break;
          case 'double_sharps':
            accidentalsSet = true;
            probability = 1;
            break;
          case 'double_flats':
            accidentalsSet = true;
            probability = 1;
            break;
          case 'naturals':
            naturalsSet = true;
            naturalSkillSet = true;
            probability = 2;
            break;
          case 'treble_c_g':
            lowSet = true;
            naturalsSet = true;
            probability = 5;
            break;
          case 'treble_a_g':
            naturalsSet = true;
            highNote = 'G5';
            highSet = true;
            if (!lowSet) {
              lowNote = 'A4';
              lowSet = true;
            }
            probability = 7;
            break;
          case 'treble_high_legers':
            naturalsSet = true;
            highNote = 'E6';
            highSet = true;
            if (!lowSet) {
              lowNote = 'C5';
              lowSet = true;
            }
            probability = 5;
            break;
          case 'treble_low_legers':
            naturalsSet = true;
            lowNote = 'F3';
            lowSet = true;
            if (!highSet) {
              highNote = 'F4';
              highSet = true;
            }
            probability = 5;
            break;
          case 'bass_lines':
            if (!lowSet) {
              lowNote = 'G2';
              lowSet = true;
            }
            if (!highSet) {
              highNote = 'A3';
              highSet = true;
            }
            probability = 5;
            break;
          case 'bass_spaces':
            probability = 4;
            break;
          case 'bass_high_legers':
            highNote = 'G4';
            highSet = true;
            if (!lowSet) {
              lowNote = 'G3';
            }
            probability = 5;
            break;
          case 'bass_low_legers':
            lowNote = 'C2';
            lowSet = true;
            if (!highSet) {
              highNote = 'C3';
            }
            probability = 5;
            break;
          default:
            probability = 7;
            break;
        }
        skillObjects.push({ skill: skillString, probability });
      }, this);

    if (accidentalsSet && !naturalsSet) {
      skillObjects.push({ skill: 'naturals', probability: 3 });
    }

    if (naturalSkillSet && !accidentalsSet) {
      skillObjects.push({ skill: 'sharps', probability: 2 });
      skillObjects.push({ skill: 'flats', probability: 2 });
      skillObjects.push({ skill: 'double_flats', probability: 1 });
      skillObjects.push({ skill: 'double_sharps', probability: 1 });
    }

    this.options.lowestNote = lowNote;
    this.options.highestNote = highNote;
    const notes = generator.multipleSkillsGenerate(
      skillObjects,
      this.options.maxQuestions! * 4,
      this.options
    );

    const questions = [];
    while (notes.length) {
      questions.push(notes.splice(0, 4));
    }

    // be sure we have some naturals from time to time:
    if (naturalSkillSet) {
      questions.forEach((questionNotes) => {
        if (Math.random() > 0.3) return;
        // Find a note with an accidental:
        const accidentalNotes = questionNotes.slice(0, 3).filter((note) => note.accidental);
        if (accidentalNotes.length) {
          const firstNote = accidentalNotes[Math.floor(Math.random() * accidentalNotes.length)];
          const firstIndex = questionNotes.indexOf(firstNote);
          const naturalIndex =
            Math.floor(Math.random() * (questionNotes.length - firstIndex)) + firstIndex;
          questionNotes[naturalIndex] = firstNote.clone().setAccidental('');
        }
      });
    }

    const questionStrings: string[] = questions.map((questionNotes) =>
      questionNotes.map((note) => note.toString()).join('/')
    );

    return questionStrings.map(
      (qstr: string, questionNumber: number): NoteReadingQuestion =>
        this.processQuestion(qstr, questionNumber)
    );
  }

  addListeners(): void {
    this.piano!.addNoteOnListener(this.check, this);
  }

  newQuestion(): void {
    this.progressTracker!.start(this.targetSpeed);
    const question = this.questions[this.questionNumber] as NoteReadingQuestion;
    this.currentQuestion = question;

    this.notation!.music = question.music;
    this.notation!.render();

    this.verifyKeyboardRange(question);
    this.displayInstructions();
  }

  displayInstructions(): void {
    super.displayInstructions();
    if (this.lastMidi) {
      window.setTimeout(() => {
        // this.piano.clearNoteColoring(this.lastMidi);
        this.piano!.clearColors();
      }, 0);
    }
  }

  // Checks to make sure all of the notes in the melody are playable
  // on the given keyboard:
  verifyKeyboardRange(question: NoteReadingQuestion): void {
    let lowestPianoNote = this.piano!.lowestNote;
    let highestPianoNote = this.piano!.highestNote;

    const { lowestNote, highestNote } = question;

    let outOfBounds = false;
    if (lowestNote.isLessThan(lowestPianoNote)) {
      lowestPianoNote = lowestNote.getNoteAtGeneric(-2);
      outOfBounds = true;
    }
    if (highestNote.isGreaterThan(highestPianoNote)) {
      highestPianoNote = highestNote.getNoteAtGeneric(2);
      outOfBounds = true;
    }

    if (outOfBounds) this.piano!.setBoundaries(lowestNote, highestNote);
  }

  check(studentMidi: number): void | Promise<any> {
    if (this.currentQuestion.isOver) return undefined;
    if (this.rightAnswerDelay) return undefined;

    const question = this.currentQuestion;
    this.checkMark!.off();

    // Ignore octaves in testMode -- even if it wasn't set on the options, since they get no warning.
    const options = {
      ...this.options,
      ignoreOctave: this.options.ignoreOctave || this.options.testMode || false,
      testMode: this.options.testMode || false,
    };
    const {
      correct,
      warning,
      newQuestion = false,
      midiNumber,
    } = question.check(studentMidi, options);

    this.checkingMidiNumber = midiNumber;
    this.questionIsFinished = newQuestion;

    if (!warning || this.options.testMode) this.piano!.clearColors();

    // GCR TODO -- this
    if (this.optionsTrue('testMode')) {
      this.lastMidi = midiNumber;
      this.piano!.colorNote(studentMidi);
      this.checkPointCheck(newQuestion);
      return undefined;
    }

    if (warning) {
      return this.checkMark!.warn(warning);
    }
    if (correct === true) this.piano!.colorNote(studentMidi);
    else if (correct === false) this.piano!.colorNote(studentMidi, 'red');
    return correct ? this.rightAnswer() : this.wrongAnswer();
  }

  rightAnswer(): void {
    this.notation!.render();
    this.playAnswer();
    const gameOver = this.questionIsFinished && this.gameIsOver(true);

    if (this.settings.progressTracker) {
      this.progressTracker!.rightAnswer(this.currentQuestion.skills, gameOver);
    }

    // Notify student of right answer, log & track it all:
    if (this.questionIsFinished) {
      this.questionNumber++;
      if (this.settings.checkMark) this.checkMark!.check();
      this.rightAnswerCallbacks.forEach((callback) => {
        callback(gameOver);
      }, this);
    }

    // If the game's not over, go on to the next question.
    if (gameOver) this.gameOver();
    else if (this.questionIsFinished) this.newQuestion();
  }

  wrongAnswer(): void {
    if (this.settings.progressTracker) {
      this.progressTracker!.wrongAnswer(this.currentQuestion.skills);
    }
    // Notify student of wrong answer:
    if (this.settings.checkMark) this.checkMark!.x();
    this.wrongAnswerCallbacks.forEach((callback) => {
      callback();
    }, this);
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(question: string): Promise<NoteReadingQuestion> {
    return this.loadingPromise.then(() => {
      this.questionNumber = 0;
      this.generateQuestions([question]);
      this.newQuestion();
      this.resize();
      return Promise.resolve(this.currentQuestion);
    });
  }

  checkPointCheck(questionIsOver: boolean): void {
    if (!questionIsOver) {
      this.notation!.render();
      return;
    }

    this.checkpointAnswer();
  }

  // Called after all parts of question have been answered:
  getAnswerAndData(): AnswerAndData {
    const question = this.currentQuestion;
    const rightAnswers = question.percent * question.noteHolders.length;
    const wrongAnswers = question.noteHolders.length - rightAnswers;

    // Tally how many right & wrong, and highlight the wrong ones,
    // and export the SVG:
    question.noteHolders.forEach((nh: NoteHolder, i: number) => {
      delete nh.color;
      if (!question.isRight[i]) nh.color = 'red';
      else nh.color = 'green';
    });

    const studentAnswerText = question.guesses.map((note) => note.toString()).join(' ');

    this.notation!.render();
    const SVG = this.notation!.exportSVG();
    question.noteHolders.forEach((holder: NoteHolder) => {
      delete holder.color;
    });

    // Tally the skills:
    const skills = question.allSkills;

    const answerAndData = {
      question: question.music.toString(),
      questionType: 'NoteReadingPiano',
      questionText: this.instructionsDiv!.innerHTML,
      questionSVG: SVG,
      studentAnswerText,
      correctAnswerText: question.noteHolders
        .map((nh: NoteHolder) => nh.notes[0]!.toString())
        .join(' '),
      rightAnswers,
      wrongAnswers,
      skills,
    };

    return answerAndData;
  }

  gameOver(): void {
    this.instructionsDiv!.innerHTML = 'Nice work!';
    this.instructionsDiv!.setAttribute('role', 'alert');
    try {
      this.displayDiv.removeChild(this.pianoDiv!);
      // eslint-disable-next-line no-empty
    } catch (err) {}

    super.gameOver();
  }

  playAnswer(): void {
    if (typeof this.lastMidi !== 'undefined') {
      this.player!.noteOff(this.lastMidi);
    }
    this.lastMidi = this.checkingMidiNumber as number;
    this.player!.noteOn(this.checkingMidiNumber!, 0.8);
  }

  static registry: Record<string, any>[] = [
    {
      description: 'Play music on the piano (treble clef)',
      alsoTrains: ['piano_c_g', 'piano_a_b'],
      skills: [
        'treble_c_g',
        'treble_a_g',
        'treble_low_legers',
        'treble_high_legers',
        'sharps',
        'flats',
        'naturals',
        'double_sharps',
        'double_flats',
      ],
      options: { clef: 'treble' },
    },
    {
      description: 'Play music on the piano (bass clef)',
      alsoTrains: ['piano_c_g', 'piano_a_b'],
      skills: [
        'bass_lines',
        'bass_spaces',
        'bass_high_legers',
        'bass_low_legers',
        'sharps',
        'flats',
        'naturals',
        'double_sharps',
        'double_flats',
      ],
      options: { clef: 'bass' },
    },
  ];
}
